import { useEffect, useMemo, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import { useChannels, useDirectMessages } from "./useChatData";
import { fetchVisibleUsers, emailForUid } from "./directoryScope";
import { parseMeta } from "./status";
import Avatar from "./Avatar";

/**
 * Forward a message to another conversation. Text forwards verbatim;
 * attachments forward as a link to the hosted file.
 */
export default function ForwardModal({
  message,
  onClose,
  onDone,
}: {
  message: CometChat.BaseMessage;
  onClose: () => void;
  onDone: (targetName: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [sendingTo, setSendingTo] = useState<string | null>(null);
  const { groups } = useChannels();
  const { conversations } = useDirectMessages();
  const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();

  // Forwarding is a discovery surface like any other — it can reach
  // someone you've never messaged, so it gets the same domain scoping.
  const [users, setUsers] = useState<CometChat.User[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchVisibleUsers(meUid)
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch((e) => console.warn("forward target list failed", e));
    return () => {
      cancelled = true;
    };
  }, [meUid]);

  const targets = useMemo(() => {
    const channelItems = groups.map((g) => ({
      key: `group:${g.getGuid()}`,
      id: g.getGuid(),
      type: "group" as const,
      label: g.getName(),
      kind: "Channel",
    }));
    const dmItems = conversations.map((c) => {
      const other = c.getConversationWith() as CometChat.User;
      return {
        key: `user:${other.getUid()}`,
        id: other.getUid(),
        type: "user" as const,
        label: other.getName(),
        kind: "Direct message",
      };
    });
    const peopleItems = users.map((u) => ({
      key: `user:${u.getUid()}`,
      id: u.getUid(),
      type: "user" as const,
      label: u.getName(),
      email: emailForUid(u.getUid()) || (parseMeta(u).dbmEmail as string | undefined),
      kind: "Person",
    }));

    const seen = new Set<string>();
    const all = [...channelItems, ...dmItems, ...peopleItems].filter((item) => {
      if (seen.has(item.key)) return false;
      seen.add(item.key);
      return true;
    });
    const q = query.trim().toLowerCase();
    return q
      ? all.filter(
          (i) =>
            i.label.toLowerCase().includes(q) ||
            ("email" in i && i.email?.toLowerCase().includes(q))
        )
      : all;
  }, [groups, conversations, users, query]);

  const forwardText = (): string => {
    if (message.getType() === "text") return (message as CometChat.TextMessage).getText();
    const media = message as CometChat.MediaMessage;
    const attachment = media.getAttachment?.();
    const url = attachment?.getUrl?.();
    const name = attachment?.getName?.() || "attachment";
    return url ? `Forwarded ${name}: ${url}` : "Forwarded attachment";
  };

  const forwardTo = async (target: { id: string; type: "user" | "group"; label: string }) => {
    if (sendingTo) return;
    setSendingTo(target.id);
    try {
      const msg = new CometChat.TextMessage(target.id, forwardText(), target.type);
      await CometChat.sendMessage(msg);
      onDone(target.label);
      onClose();
    } catch (e) {
      console.warn("forward failed", e);
      setSendingTo(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <b>Forward to</b>
          <button className="hbtn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-section">
          <div className="modal-row">
            <input
              type="text"
              autoFocus
              placeholder="Search channels and people…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="modal-section">
          <div className="modal-list">
            {targets.length === 0 && <div className="sect-empty">No matches</div>}
            {targets.map((t) => (
              <button
                key={t.key}
                className="modal-user"
                disabled={!!sendingTo}
                onClick={() => void forwardTo(t)}
              >
                {/* uid only for people — a group's id has no avatar. */}
                <Avatar uid={t.type === "user" ? t.id : undefined} name={t.label} size="dav" />
                <span className="nm">{sendingTo === t.id ? "Sending…" : t.label}</span>
                <span className="ext" style={{ marginLeft: "auto" }}>{t.kind}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
